import { useCallback, useEffect, useState } from 'react'

// Response shapes of the Hono API (src/api/*). Dates arrive as ISO strings.

export type RecType = 'buy' | 'sell' | 'hold' | 'watch' | 'avoid'

export interface Spark {
  closes: number[] | null
  entryIndex: number
}

export interface Rec {
  id: string
  ticker: string | null
  company_name: string | null
  type: RecType | null
  conviction: number | null
  price_target: number | null
  horizon: string | null
  thesis: string
  summary: string | null
  timestamp_seconds: number
  timestamp_label: string
  clip_end_seconds: number | null
  stance_source: string | null
  episode_id: string
  episode_title: string
  episode_pub_date: string
  podcast_id: string
  podcast_name: string
  podcast_image_url: string | null
  entry_ts: string | null
  entry_price: number | null
  last_ts: string | null
  last_price: number | null
  move_pct: number | null
  pick_return_pct: number | null
  pick_return_usd: number | null
  scored: boolean
  spark: Spark
}

export interface PricePoint {
  ts: string
  close: number
}

export interface RecDetail {
  recommendation: Rec & {
    episode_audio_url: string
    episode_duration_seconds: number | null
    other_mention_count: number
  }
  prices: PricePoint[]
}

export interface StockRow {
  ticker: string
  name: string
  closes: number[] | null
  last_ts: string | null
  last_close: number | null
  prev_close: number | null
  day_change_pct: number | null
  buy_count: number
  sell_count: number
  hold_count: number
  mention_count: number
  last_mentioned_at: string
}

export interface StockDetail {
  stock: {
    ticker: string
    name: string
    exchange: string | null
    last_close: number | null
    prev_close: number | null
    day_change_pct: number | null
    last_ts: string | null
  }
  prices: PricePoint[]
  mentions: Rec[]
}

export interface PodcastRow {
  id: string
  name: string
  image_url: string | null
  tags: string[]
  episodes_analyzed: number
  pick_count: number
  last_pick_at: string | null
  tracked_since: string | null
}

export interface PickSummary {
  id: string
  ticker: string | null
  type: RecType | null
  pick_return_pct: number
  episode_pub_date: string
}

export interface PodcastDetail {
  podcast: PodcastRow
  stats: {
    pick_count: number
    scored_count: number
    win_rate_pct: number | null
    avg_return_pct: number | null
    best: PickSummary | null
    worst: PickSummary | null
  }
  // benchmark_pct: SPY over the same picks' windows; null where unavailable.
  portfolio: { ts: string; return_pct: number; open_picks: number; benchmark_pct: number | null }[]
  picks: Rec[]
}

export interface EpisodeRow {
  id: string
  title: string
  pub_date: string
  duration_seconds: number | null
  podcast_id: string
  podcast_name: string
  podcast_image_url: string | null
  pick_count: number
  tickers: string[]
}

export interface EpisodeDetail {
  episode: {
    id: string
    title: string
    pub_date: string
    audio_url: string
    duration_seconds: number | null
    podcast_id: string
    podcast_name: string
    podcast_image_url: string | null
  }
  recommendations: Rec[]
}

export interface SearchResults {
  stocks: { ticker: string; name: string }[]
  podcasts: { id: string; name: string; image_url: string | null }[]
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

export async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal, headers: { Accept: 'application/json' } })
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`)
  return res.json() as Promise<T>
}

// Responses are kept for the session so going back renders immediately (and
// scroll restoration has content to land on); each visit still refetches.
const cache = new Map<string, unknown>()

export interface ApiState<T> {
  data: T | undefined
  error: ApiError | Error | undefined
  loading: boolean
  retry: () => void
}

export function useApi<T>(path: string | null): ApiState<T> {
  const [state, setState] = useState<{ path: string | null; data?: T; error?: Error }>(() => ({
    path,
    data: path ? (cache.get(path) as T | undefined) : undefined,
  }))
  const [attempt, setAttempt] = useState(0)

  // A new path starts from its cached response, if any.
  if (state.path !== path) {
    setState({ path, data: path ? (cache.get(path) as T | undefined) : undefined })
  }

  useEffect(() => {
    if (!path) return
    const ctrl = new AbortController()
    fetchJson<T>(path, ctrl.signal).then(
      (data) => {
        cache.set(path, data)
        setState({ path, data })
      },
      (error: Error) => {
        if (ctrl.signal.aborted) return
        setState((s) => (s.path === path ? { ...s, error } : s))
      }
    )
    return () => ctrl.abort()
  }, [path, attempt])

  const retry = useCallback(() => {
    setState((s) => ({ ...s, error: undefined }))
    setAttempt((n) => n + 1)
  }, [])

  const current = state.path === path ? state : { data: undefined, error: undefined }
  return {
    data: current.data,
    error: current.data === undefined ? current.error : undefined,
    loading: current.data === undefined && !current.error,
    retry,
  }
}

export const api = {
  feed: (podcastId?: string) => `/api/recommendations${podcastId ? `?podcastId=${encodeURIComponent(podcastId)}` : ''}`,
  rec: (id: string) => `/api/recommendations/${encodeURIComponent(id)}`,
  stocks: () => '/api/stocks',
  stock: (ticker: string) => `/api/stocks/${encodeURIComponent(ticker)}`,
  podcasts: () => '/api/podcasts',
  podcast: (id: string) => `/api/podcasts/${encodeURIComponent(id)}`,
  episodes: () => '/api/episodes',
  episode: (id: string) => `/api/episodes/${encodeURIComponent(id)}`,
  transcript: (id: string) => `/api/episodes/${encodeURIComponent(id)}/transcript`,
  search: (q: string) => `/api/search?q=${encodeURIComponent(q)}`,
}
